import { useState, useEffect } from 'react';
import { DialogPosition } from '../types';

interface UseDialogPositionProps {
  dialogSize: { width: number; height: number };
  isOpen: boolean;
}

export default function useDialogPosition({ dialogSize, isOpen }: UseDialogPositionProps) {
  const [position, setPosition] = useState<DialogPosition>({
    x: window.innerWidth / 2 - 400,
    y: window.innerHeight / 2 - 300,
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (!anchorEl && isOpen) {
      const bodyRect = document.body.getBoundingClientRect();
      const centerX = bodyRect.width / 2;
      const centerY = bodyRect.height / 2;
      const div = document.createElement('div');
      div.style.position = 'absolute';
      div.style.left = `${centerX}px`;
      div.style.top = `${centerY}px`;
      document.body.appendChild(div);
      setAnchorEl(div);
    }

    return () => {
      if (anchorEl) {
        anchorEl.remove();
        setAnchorEl(null);
      }
    };
  }, [anchorEl, isOpen]);

  useEffect(() => {
    const handleWindowResize = () => {
      setPosition((prev) => {
        const maxX = window.innerWidth - dialogSize.width;
        const maxY = window.innerHeight - dialogSize.height;

        return {
          x: Math.min(Math.max(0, prev.x), maxX),
          y: Math.min(Math.max(0, prev.y), maxY),
        };
      });
    };

    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [dialogSize]);

  const updatePosition = (x: number, y: number) => {
    setPosition({ x, y });
  };

  return {
    position,
    updatePosition,
    anchorEl,
  };
}
